import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AppShell, PageHeader } from "../components/AppShell";
import { IngredientPicker } from "../components/IngredientPicker";
import { PrintableOrder } from "../components/PrintableOrder";
import { UnitPills } from "../components/UnitPills";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { catColor, catLabel, euro, euroPrecise, formatQty } from "../lib/constants";
import {
    Plus,
    Trash2,
    Truck,
    Crown,
    Lock,
    Sparkles,
    Printer,
    Copy,
    AlertTriangle,
    CheckCircle2,
    ArrowRight,
    PackageX,
    XCircle,
    ChefHat,
    Layers,
    Star,
} from "lucide-react";
import { toast } from "sonner";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Mappa una riga del carrello backend in shape locale {tmp_id, ingredient, quantity, unit, forced_supplier_id, source, recipe_name}
const mapCartLine = (l) => ({
    tmp_id: l.tmp_id,
    ingredient: {
        id: l.ingredient_id,
        name: l.ingredient_name,
        category: l.ingredient_category,
        unit: l.ingredient_unit,
    },
    quantity: l.quantity,
    unit: l.unit,
    forced_supplier_id: l.forced_supplier_id || "",
    source: l.source || "manual",
    recipe_name: l.recipe_name || null,
});

export default function Orders() {
    const { user } = useAuth();
    const [billing, setBilling] = useState(null);
    const [ingredients, setIngredients] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [lines, setLines] = useState([]); // {tmp_id, ingredient, quantity, unit, forced_supplier_id, source, recipe_name}
    const [cartLoading, setCartLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [computing, setComputing] = useState(false);
    const [unassignedDrafts, setUnassignedDrafts] = useState({}); // ingredient_id -> {supplier_id, unit_price}
    const [confirmAction, setConfirmAction] = useState(null); // {kind: 'cancel'|'complete'} | null
    const [mergeConfirm, setMergeConfirm] = useState(null); // {tmp_id, ingredient_name, previous_quantity, added_quantity, total_quantity, unit}
    const printRef = useRef(null);

    const reloadCart = async () => {
        try {
            const { data } = await api.get("/orders/cart");
            setLines((data.lines || []).map(mapCartLine));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCartLoading(false);
        }
    };

    useEffect(() => {
        api.get("/billing/status").then(({ data }) => setBilling(data));
        api.get("/ingredients").then(({ data }) => setIngredients(data));
        api.get("/suppliers").then(({ data }) => setSuppliers(data));
        reloadCart();
    }, []);

    const proActive = billing?.pro_active;

    const addLine = async (ing) => {
        setPickerOpen(false);
        try {
            const { data } = await api.post("/orders/cart/add", {
                lines: [{
                    ingredient_id: ing.id,
                    quantity: 1,
                    unit: ing.unit,
                    source: "manual",
                }],
            });
            setLines((data.lines || []).map(mapCartLine));
            setResult(null);
            if (data.merged && data.merged.length > 0) {
                // L'ingrediente era già nel carrello: accorpato, chiedi conferma quantità.
                setMergeConfirm(data.merged[0]);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const confirmMergeQuantity = async (newQty) => {
        if (!mergeConfirm) return;
        const tmp_id = mergeConfirm.tmp_id;
        const q = parseFloat(newQty);
        if (!q || q <= 0) {
            toast.error("Quantità non valida");
            return;
        }
        // Se quantità invariata rispetto al totale accorpato, niente chiamata
        if (Math.abs(q - mergeConfirm.total_quantity) > 1e-6) {
            try {
                const { data } = await api.patch(`/orders/cart/${tmp_id}`, { quantity: q });
                setLines((data.lines || []).map(mapCartLine));
            } catch (e) {
                toast.error(formatApiError(e));
            }
        }
        setMergeConfirm(null);
    };

    const updateLine = async (tmp_id, patch) => {
        // Optimistic update
        setLines((prev) =>
            prev.map((l) => (l.tmp_id === tmp_id ? { ...l, ...patch } : l))
        );
        setResult(null);
        try {
            const body = {};
            if (patch.quantity !== undefined) body.quantity = parseFloat(patch.quantity) || 0;
            if (patch.unit !== undefined) body.unit = patch.unit;
            if (patch.forced_supplier_id !== undefined)
                body.forced_supplier_id = patch.forced_supplier_id || "";
            // Skip empty quantity (user is typing)
            if (body.quantity !== undefined && body.quantity <= 0) return;
            const { data } = await api.patch(`/orders/cart/${tmp_id}`, body);
            setLines((data.lines || []).map(mapCartLine));
        } catch (e) {
            toast.error(formatApiError(e));
            reloadCart();
        }
    };

    const removeLine = async (tmp_id) => {
        try {
            const { data } = await api.delete(`/orders/cart/${tmp_id}`);
            setLines((data.lines || []).map(mapCartLine));
            setResult(null);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const compute = async () => {
        if (lines.length === 0) {
            toast.error("Aggiungi almeno un ingrediente");
            return;
        }
        setComputing(true);
        try {
            const payload = {
                lines: lines.map((l) => ({
                    ingredient_id: l.ingredient.id,
                    quantity: parseFloat(l.quantity) || 0,
                    unit: l.unit,
                    forced_supplier_id: l.forced_supplier_id || null,
                })),
            };
            const { data } = await api.post("/orders/suggest", payload);
            setResult(data);
            if (data.unassigned?.length > 0) {
                toast.warning(
                    `${data.unassigned.length} ingrediente/i senza fornitore a listino`
                );
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setComputing(false);
        }
    };

    const performClear = async (kind) => {
        try {
            await api.delete("/orders/cart");
            setLines([]);
            setResult(null);
            setUnassignedDrafts({});
            if (kind === "complete") {
                toast.success("Ordine effettuato. Carrello svuotato.");
            } else {
                toast.success("Ordine cancellato.");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setConfirmAction(null);
        }
    };

    const updateDraft = (ingredient_id, patch) => {
        setUnassignedDrafts((prev) => ({
            ...prev,
            [ingredient_id]: { ...prev[ingredient_id], ...patch },
        }));
    };

    const assignToSupplier = (line) => {
        const draft = unassignedDrafts[line.ingredient_id] || {};
        if (!draft.supplier_id) {
            toast.error("Seleziona un fornitore");
            return;
        }
        const price = parseFloat(draft.unit_price);
        if (!price || price <= 0) {
            toast.error("Inserisci un prezzo unitario valido");
            return;
        }
        const supplier = suppliers.find((s) => s.id === draft.supplier_id);
        if (!supplier) {
            toast.error("Fornitore non trovato");
            return;
        }
        const lineTotal = round2(price * (line.quantity_base || 0));
        const newLine = {
            ...line,
            unit_price: price,
            line_total: lineTotal,
            forced: true,
            manual: true,
        };
        setResult((prev) => {
            if (!prev) return prev;
            const groups = [...prev.groups];
            const idx = groups.findIndex((g) => g.supplier_id === supplier.id);
            if (idx >= 0) {
                const g = groups[idx];
                groups[idx] = {
                    ...g,
                    lines: [...g.lines, newLine],
                    total: round2(g.total + lineTotal),
                };
            } else {
                groups.push({
                    supplier_id: supplier.id,
                    supplier_name: supplier.name,
                    supplier_email: supplier.email || "",
                    supplier_phone: supplier.phone || "",
                    lines: [newLine],
                    total: lineTotal,
                });
            }
            return {
                ...prev,
                groups,
                unassigned: prev.unassigned.filter(
                    (u) => u.ingredient_id !== line.ingredient_id
                ),
                grand_total: round2(prev.grand_total + lineTotal),
            };
        });
        setUnassignedDrafts((prev) => {
            const c = { ...prev };
            delete c[line.ingredient_id];
            return c;
        });
        toast.success(`${line.ingredient_name} assegnato a ${supplier.name}`);
    };

    const copySummary = async () => {
        if (!result) return;
        const txt = buildSummaryText(result, user);
        try {
            await navigator.clipboard.writeText(txt);
            toast.success("Riepilogo copiato negli appunti");
        } catch {
            toast.error("Copia non riuscita");
        }
    };

    const printSummary = () => {
        window.print();
    };

    if (!billing) {
        return (
            <AppShell>
                <div className="py-16 text-center text-sm text-brand-muted">Caricamento…</div>
            </AppShell>
        );
    }

    if (!proActive) {
        return (
            <AppShell>
                <PageHeader eyebrow="Piano Pro" title="Ordini fornitore intelligenti" />
                <ProUpsellCard />
            </AppShell>
        );
    }

    return (
        <AppShell>
            <div className="print:hidden">
                <PageHeader
                    eyebrow="Pro · Approvvigionamento"
                    title="Ordini fornitore intelligenti"
                    subtitle="Aggiungi gli ingredienti che ti servono: il sistema sceglie il fornitore migliore per ognuno."
                    right={
                        <button
                            onClick={() => setPickerOpen(true)}
                            data-testid="orders-add-btn"
                            className="hidden lg:inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-navyLight transition shadow-card"
                        >
                            <Plus size={16} /> Aggiungi voce
                        </button>
                    }
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-6 print:hidden">
                <Cart
                    lines={lines}
                    suppliers={suppliers}
                    cartLoading={cartLoading}
                    onUpdate={updateLine}
                    onRemove={removeLine}
                    onAdd={() => setPickerOpen(true)}
                    onCompute={compute}
                    computing={computing}
                    onCancelOrder={() => setConfirmAction({ kind: "cancel" })}
                    onCompleteOrder={() => setConfirmAction({ kind: "complete" })}
                />
                <ResultPanel
                    result={result}
                    suppliers={suppliers}
                    drafts={unassignedDrafts}
                    onUpdateDraft={updateDraft}
                    onAssign={assignToSupplier}
                    onCopy={copySummary}
                    onPrint={printSummary}
                />
            </div>

            {confirmAction && (
                <ConfirmActionDialog
                    kind={confirmAction.kind}
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={() => performClear(confirmAction.kind)}
                />
            )}

            {mergeConfirm && (
                <MergeConfirmDialog
                    info={mergeConfirm}
                    onCancel={() => setMergeConfirm(null)}
                    onConfirm={confirmMergeQuantity}
                />
            )}

            {/* Print version - portal su body per evitare clipping del layout */}
            {result && createPortal(
                <div className="print-portal" ref={printRef}>
                    <PrintableOrder result={result} user={user} />
                </div>,
                document.body
            )}

            <button
                onClick={() => setPickerOpen(true)}
                data-testid="orders-fab"
                className="lg:hidden fixed right-5 bottom-24 z-40 h-14 w-14 rounded-full bg-brand-gold text-brand-navy shadow-gold flex items-center justify-center print:hidden"
            >
                <Plus size={24} strokeWidth={2.6} />
            </button>

            <IngredientPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                ingredients={ingredients}
                onPick={addLine}
            />
        </AppShell>
    );
}

function Cart({ lines, suppliers, cartLoading, onUpdate, onRemove, onAdd, onCompute, computing, onCancelOrder, onCompleteOrder }) {
    return (
        <div className="bg-paper rounded-2xl border border-brand-line shadow-card overflow-hidden">
            <div className="px-5 sm:px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="eyebrow text-brand-gold">Lista ordine</div>
                    <div className="font-display text-xl text-brand-ink mt-1">
                        {cartLoading ? "Caricamento…" : `${lines.length} ${lines.length === 1 ? "voce" : "voci"}`}
                    </div>
                    <div className="mt-1 text-[11px] text-brand-muted">
                        Il carrello resta salvato finché non confermi l'ordine o lo cancelli.
                    </div>
                </div>
                {lines.length > 0 && (
                    <button
                        onClick={onCancelOrder}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-brand-danger hover:bg-red-50 px-3 py-1.5 rounded-full transition"
                        data-testid="orders-cancel-btn"
                    >
                        <XCircle size={13} /> Cancella ordine
                    </button>
                )}
            </div>

            {lines.length === 0 ? (
                <div className="p-12 text-center">
                    <Truck className="mx-auto text-brand-muted mb-3" size={36} strokeWidth={1.4} />
                    <div className="font-display text-xl text-brand-ink">Nessuna voce</div>
                    <p className="text-sm text-brand-muted mt-1">
                        Aggiungi gli ingredienti che vuoi ordinare. Puoi anche aggiungere
                        ingredienti dalle pagine "Alimenti" e "Piatti".
                    </p>
                    <button
                        onClick={onAdd}
                        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navyLight transition"
                        data-testid="orders-empty-add"
                    >
                        <Plus size={14} /> Aggiungi voce
                    </button>
                </div>
            ) : (
                <div className="divide-y divide-brand-line">
                    {lines.map((line) => (
                        <CartRow
                            key={line.tmp_id}
                            line={line}
                            suppliers={suppliers}
                            onUpdate={(patch) => onUpdate(line.tmp_id, patch)}
                            onRemove={() => onRemove(line.tmp_id)}
                        />
                    ))}
                </div>
            )}

            <div className="px-5 sm:px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-between gap-3 flex-wrap">
                <button
                    onClick={onAdd}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-line text-brand-ink text-sm font-semibold bg-paper hover:bg-white transition"
                    data-testid="orders-add-secondary"
                >
                    <Plus size={14} /> Aggiungi voce
                </button>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    {lines.length > 0 && (
                        <button
                            onClick={onCompleteOrder}
                            data-testid="orders-complete-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-success/10 text-brand-success border border-brand-success/40 text-sm font-bold hover:bg-brand-success hover:text-white transition"
                        >
                            <CheckCircle2 size={14} /> Ordine effettuato
                        </button>
                    )}
                    <button
                        onClick={onCompute}
                        disabled={lines.length === 0 || computing}
                        data-testid="orders-compute"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-gold text-brand-navy text-sm font-bold hover:bg-brand-goldDark disabled:opacity-50 transition shadow-gold"
                    >
                        <Sparkles size={14} />
                        {computing ? "Calcolo…" : "Calcola ordine"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function MergeConfirmDialog({ info, onCancel, onConfirm }) {
    const [qty, setQty] = useState(info.total_quantity);
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
            onClick={onCancel}
            data-testid="merge-confirm-dialog"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={(e) => {
                    e.preventDefault();
                    onConfirm(qty);
                }}
                className="w-full max-w-md bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden"
            >
                <div className="px-6 py-5 border-b border-brand-line">
                    <div className="h-11 w-11 rounded-full flex items-center justify-center mb-3 bg-brand-gold/15 text-brand-gold">
                        <Layers size={20} />
                    </div>
                    <h3 className="font-display text-2xl text-brand-ink">
                        {info.ingredient_name} era già nel carrello
                    </h3>
                    <p className="mt-2 text-sm text-brand-muted">
                        Ho accorpato le righe. Conferma la quantità totale:
                    </p>
                    <div className="mt-4 rounded-xl bg-brand-cream border border-brand-line p-3 text-[12px] text-brand-ink/80 space-y-1">
                        <div className="flex justify-between">
                            <span>Precedente:</span>
                            <span className="font-semibold">
                                {info.previous_quantity} {info.unit}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>Aggiunta:</span>
                            <span className="font-semibold">
                                +{info.added_quantity} {info.added_unit}
                            </span>
                        </div>
                        <div className="flex justify-between pt-1 border-t border-brand-line">
                            <span className="font-bold">Totale accorpato:</span>
                            <span className="font-bold text-brand-navy">
                                {info.total_quantity} {info.unit}
                            </span>
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1.5">
                            Quantità finale ({info.unit})
                        </label>
                        <input
                            type="number"
                            step="0.001"
                            min="0"
                            value={qty}
                            autoFocus
                            onChange={(e) => setQty(e.target.value)}
                            data-testid="merge-qty-input"
                            className="w-full px-3 py-2.5 rounded-md bg-white border border-brand-line text-base focus:outline-none focus:border-brand-gold"
                        />
                        <p className="mt-1.5 text-[11px] text-brand-muted">
                            Modifica se vuoi un totale diverso (es. se hai sbagliato l'ordine precedente).
                        </p>
                    </div>
                </div>
                <div className="px-6 py-4 bg-brand-cream flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="merge-confirm-skip"
                        className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                    >
                        Mantieni totale
                    </button>
                    <button
                        type="submit"
                        data-testid="merge-confirm-ok"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition"
                    >
                        <CheckCircle2 size={14} /> Conferma
                    </button>
                </div>
            </form>
        </div>
    );
}

function ConfirmActionDialog({ kind, onCancel, onConfirm }) {
    const isCancel = kind === "cancel";
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
            onClick={onCancel}
            data-testid="orders-confirm-dialog"
        >
            <div
                className="w-full max-w-md bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-5 border-b border-brand-line">
                    <div
                        className={`h-11 w-11 rounded-full flex items-center justify-center mb-3 ${
                            isCancel
                                ? "bg-red-50 text-brand-danger"
                                : "bg-emerald-50 text-brand-success"
                        }`}
                    >
                        {isCancel ? <XCircle size={20} /> : <CheckCircle2 size={20} />}
                    </div>
                    <h3 className="font-display text-2xl text-brand-ink">
                        {isCancel ? "Cancella ordine?" : "Confermi l'ordine effettuato?"}
                    </h3>
                    <p className="mt-2 text-sm text-brand-muted">
                        {isCancel
                            ? "Tutte le voci nel carrello verranno rimosse. Questa azione non si può annullare."
                            : "Marca l'ordine come effettuato e svuota il carrello. Procedi solo se hai inviato/stampato l'ordine ai fornitori."}
                    </p>
                </div>
                <div className="px-6 py-4 bg-brand-cream flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="orders-confirm-no"
                        className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        data-testid="orders-confirm-yes"
                        className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold transition ${
                            isCancel
                                ? "bg-brand-danger text-white hover:opacity-90"
                                : "bg-brand-success text-white hover:opacity-90"
                        }`}
                    >
                        {isCancel ? (
                            <>
                                <XCircle size={14} /> Sì, cancella
                            </>
                        ) : (
                            <>
                                <CheckCircle2 size={14} /> Sì, svuota carrello
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CartRow({ line, suppliers, onUpdate, onRemove }) {
    const ing = line.ingredient;
    return (
        <div
            className="px-5 sm:px-6 py-4 flex flex-col gap-3"
            data-testid={`order-line-${line.tmp_id}`}
        >
            {/* Riga 1: nome + cestino */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <span
                        className="h-10 w-1.5 rounded-full shrink-0 mt-0.5"
                        style={{ background: catColor(ing.category) }}
                    />
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-brand-ink truncate">
                            {ing.name}
                        </div>
                        <div className="text-[11px] text-brand-muted uppercase tracking-[0.16em]">
                            {catLabel(ing.category)}
                        </div>
                        {line.recipe_name && (
                            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-gold/10 text-[10px] text-brand-gold font-bold uppercase tracking-wider">
                                <ChefHat size={9} /> {line.recipe_name}
                            </div>
                        )}
                    </div>
                </div>
                <button
                    onClick={onRemove}
                    aria-label="Rimuovi"
                    className="h-9 w-9 rounded-full text-brand-muted hover:bg-red-50 hover:text-red-600 transition flex items-center justify-center shrink-0"
                >
                    <Trash2 size={15} />
                </button>
            </div>

            {/* Riga 2: quantità + UM (editabile direttamente) */}
            <div className="flex items-end gap-3 flex-wrap">
                <div className="w-24 shrink-0">
                    <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                        Quantità
                    </label>
                    <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={line.quantity}
                        onChange={(e) => onUpdate({ quantity: e.target.value })}
                        className="w-full px-2.5 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                        data-testid={`order-qty-${line.tmp_id}`}
                    />
                </div>
                <div className="shrink-0">
                    <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                        UM
                    </label>
                    <UnitPills
                        value={line.unit}
                        onChange={(v) => onUpdate({ unit: v })}
                        testid={`order-um-${line.tmp_id}`}
                    />
                </div>
            </div>

            {/* Riga 3: fornitore (full width, separato dalla UM) */}
            <div className="w-full">
                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                    Fornitore
                </label>
                <select
                    value={line.forced_supplier_id}
                    onChange={(e) => onUpdate({ forced_supplier_id: e.target.value })}
                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                    data-testid={`order-supplier-${line.tmp_id}`}
                >
                    <option value="">Auto (miglior prezzo)</option>
                    {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}

function ResultPanel({ result, suppliers, drafts, onUpdateDraft, onAssign, onCopy, onPrint }) {
    if (!result) {
        return (
            <div className="bg-paper rounded-2xl border border-dashed border-brand-line p-8 text-center text-sm text-brand-muted">
                <Sparkles className="mx-auto text-brand-muted mb-3" size={28} strokeWidth={1.4} />
                Calcola l'ordine per vedere lo split per fornitore.
            </div>
        );
    }
    const totalLines = result.groups.reduce((s, g) => s + g.lines.length, 0);
    return (
        <div className="space-y-4" data-testid="orders-result">
            <div className="bg-paper rounded-2xl border border-brand-line shadow-card p-5">
                <div className="flex items-baseline justify-between">
                    <div>
                        <div className="eyebrow text-brand-gold">Riepilogo ordine</div>
                        <div className="mt-1 text-sm text-brand-muted">
                            {result.groups.length} {result.groups.length === 1 ? "fornitore" : "fornitori"} · {totalLines} righe
                            {result.unassigned?.length > 0 && (
                                <> · <span className="text-amber-700 font-semibold">{result.unassigned.length} da associare</span></>
                            )}
                        </div>
                        {result.preferred_only && (
                            <div
                                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 px-2.5 py-0.5 text-[11px] font-bold"
                                data-testid="orders-preferred-mode"
                                title="Lo splitting considera solo i fornitori marcati come preferiti"
                            >
                                <Star size={11} className="fill-amber-400" />
                                Splitting tra {result.preferred_count} preferit{result.preferred_count === 1 ? "o" : "i"}
                            </div>
                        )}
                    </div>
                    <div className="font-display text-3xl text-brand-ink">{euro(result.grand_total)}</div>
                </div>
                <div className="mt-4 flex gap-2">
                    <button
                        onClick={onCopy}
                        data-testid="orders-copy"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-brand-line bg-paper text-sm font-semibold text-brand-ink hover:bg-brand-cream transition"
                    >
                        <Copy size={14} /> Copia
                    </button>
                    <button
                        onClick={onPrint}
                        data-testid="orders-print"
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navyLight transition"
                    >
                        <Printer size={14} /> Stampa
                    </button>
                </div>
            </div>

            {result.groups.map((g) => (
                <SupplierGroup key={g.supplier_id} group={g} />
            ))}

            {result.unassigned?.length > 0 && (
                <UnassignedPanel
                    items={result.unassigned}
                    suppliers={suppliers}
                    drafts={drafts}
                    onUpdateDraft={onUpdateDraft}
                    onAssign={onAssign}
                />
            )}
        </div>
    );
}

function UnassignedPanel({ items, suppliers, drafts, onUpdateDraft, onAssign }) {
    return (
        <div
            className="bg-paper rounded-2xl border border-amber-200 shadow-card overflow-hidden"
            data-testid="orders-unassigned-panel"
        >
            <div className="px-5 py-4 border-b border-amber-200 bg-amber-50 flex items-start gap-3">
                <PackageX size={20} className="text-amber-700 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-bold">
                        Senza fornitore a listino
                    </div>
                    <div className="font-display text-xl text-brand-ink mt-0.5">
                        Lista acquisti da associare a fornitore
                    </div>
                    <div className="mt-1 text-[12px] text-brand-muted">
                        {items.length} {items.length === 1 ? "articolo" : "articoli"} · assegna un
                        fornitore e un prezzo per spostarlo nell'ordine, oppure stampa la lista così
                        com'è.
                    </div>
                </div>
            </div>
            <div className="divide-y divide-brand-line">
                {items.map((l) => {
                    const draft = drafts[l.ingredient_id] || {};
                    return (
                        <div
                            key={l.ingredient_id}
                            className="px-5 py-4 grid grid-cols-12 gap-2 items-end"
                            data-testid={`orders-unassigned-row-${l.ingredient_id}`}
                        >
                            <div className="col-span-12 sm:col-span-4 flex items-start gap-3 self-start pt-1">
                                <span
                                    className="h-9 w-1.5 rounded-full shrink-0 mt-0.5"
                                    style={{ background: catColor(l.ingredient_category) }}
                                />
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-brand-ink truncate">
                                        {l.ingredient_name}
                                    </div>
                                    <div className="text-[11px] text-brand-muted">
                                        {formatQty(l.quantity, l.unit)}
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-7 sm:col-span-4">
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                                    Fornitore
                                </label>
                                <select
                                    value={draft.supplier_id || ""}
                                    onChange={(e) =>
                                        onUpdateDraft(l.ingredient_id, { supplier_id: e.target.value })
                                    }
                                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                    data-testid={`orders-unassigned-supplier-${l.ingredient_id}`}
                                >
                                    <option value="">— scegli —</option>
                                    {suppliers.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-span-5 sm:col-span-2">
                                <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1">
                                    € / {l.base_unit}
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={draft.unit_price || ""}
                                    onChange={(e) =>
                                        onUpdateDraft(l.ingredient_id, { unit_price: e.target.value })
                                    }
                                    placeholder="0,00"
                                    className="w-full px-2 py-2 rounded-md bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                                    data-testid={`orders-unassigned-price-${l.ingredient_id}`}
                                />
                            </div>
                            <div className="col-span-12 sm:col-span-2">
                                <button
                                    onClick={() => onAssign(l)}
                                    disabled={!draft.supplier_id || !draft.unit_price}
                                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-brand-navy text-white text-[12px] font-semibold hover:bg-brand-navyLight disabled:opacity-40 transition"
                                    data-testid={`orders-unassigned-assign-${l.ingredient_id}`}
                                >
                                    Assegna <ArrowRight size={12} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="px-5 py-3 bg-brand-cream border-t border-brand-line text-[12px] text-brand-muted flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <span>
                    Suggerimento: aggiungi i prezzi a listino dalla pagina{" "}
                    <Link to="/suppliers" className="text-brand-navy underline font-semibold">
                        Fornitori
                    </Link>{" "}
                    per ottenere lo split automatico al prossimo ordine.
                </span>
            </div>
        </div>
    );
}

function SupplierGroup({ group }) {
    return (
        <div className="bg-paper rounded-2xl border border-brand-line shadow-card overflow-hidden" data-testid={`order-group-${group.supplier_id}`}>
            <div className="px-5 py-4 border-b border-brand-line bg-brand-cream flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-brand-gold">Fornitore</div>
                    <div className="font-display text-xl text-brand-ink truncate">{group.supplier_name}</div>
                    {(group.supplier_email || group.supplier_phone) && (
                        <div className="mt-1 text-[12px] text-brand-muted truncate">
                            {[group.supplier_email, group.supplier_phone].filter(Boolean).join(" · ")}
                        </div>
                    )}
                </div>
                <div className="font-display text-2xl text-brand-ink shrink-0">
                    {euro(group.total)}
                </div>
            </div>
            <div className="divide-y divide-brand-line">
                {group.lines.map((l, i) => (
                    <div key={i} className="px-5 py-3 flex items-center justify-between gap-3 text-sm">
                        <div className="min-w-0">
                            <div className="font-semibold text-brand-ink truncate">{l.ingredient_name}</div>
                            <div className="text-[12px] text-brand-muted">
                                {formatQty(l.quantity, l.unit)} · {euroPrecise(l.unit_price)} / {l.base_unit}
                                {l.forced && <span className="ml-2 text-brand-gold">· forzato</span>}
                            </div>
                        </div>
                        <div className="font-semibold text-brand-ink shrink-0">{euro(l.line_total)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ProUpsellCard() {
    return (
        <div
            className="rounded-2xl p-8 text-white shadow-card relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1a2a44 0%, #243a5e 55%, #2a4268 100%)" }}
            data-testid="orders-pro-upsell"
        >
            <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center shrink-0">
                    <Crown size={22} />
                </div>
                <div className="flex-1">
                    <div className="eyebrow text-brand-gold">Funzione Pro</div>
                    <h3 className="font-display text-3xl mt-1">Ordini fornitore intelligenti</h3>
                    <p className="mt-2 text-sm text-white/75 max-w-xl">
                        Aggiungi gli ingredienti, e il sistema sceglie il fornitore migliore per ognuno
                        confrontando i listini. Puoi sempre forzare manualmente. Risparmi tempo e soldi.
                    </p>
                </div>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                    "Split automatico per miglior prezzo",
                    "Forzatura manuale fornitore",
                    "Riepilogo stampabile/copiabile",
                    "Suggerimento miglior fornitore per ingrediente",
                ].map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-white/85">
                        <CheckCircle2 size={14} className="text-brand-gold shrink-0" /> {f}
                    </div>
                ))}
            </div>
            <Link
                to="/billing"
                data-testid="orders-upgrade-link"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-6 py-3 text-sm font-bold hover:bg-brand-goldDark transition shadow-gold"
            >
                <Crown size={14} /> Passa al piano Pro · 49€/mese
            </Link>
            <div className="mt-3 text-[12px] text-white/60 inline-flex items-center gap-2">
                <Lock size={12} /> Accesso completo durante i 5 giorni di prova
            </div>
        </div>
    );
}

function buildSummaryText(result, user) {
    const lines = [];
    lines.push(`Ordine fornitori — ${user?.restaurant_name || "RENOVA360"}`);
    lines.push("");
    for (const g of result.groups) {
        lines.push(`📦 ${g.supplier_name}`);
        if (g.supplier_email) lines.push(`   ${g.supplier_email}`);
        if (g.supplier_phone) lines.push(`   ${g.supplier_phone}`);
        for (const l of g.lines) {
            lines.push(`  • ${l.ingredient_name}: ${l.quantity} ${l.unit} = ${euro(l.line_total)}`);
        }
        lines.push(`  Subtotale: ${euro(g.total)}`);
        lines.push("");
    }
    if (result.unassigned?.length) {
        lines.push(`📋 Lista acquisti da associare a fornitore:`);
        for (const u of result.unassigned) {
            lines.push(`  • ${u.ingredient_name}: ${u.quantity} ${u.unit}`);
        }
        lines.push("");
    }
    lines.push(`TOTALE: ${euro(result.grand_total)}`);
    return lines.join("\n");
}
